<template>
  <v-container style="max-width: 900px; padding-top: 2rem">
    <div class="d-flex align-center justify-space-between mb-6">
      <h1 class="text-h4 font-weight-bold">My games</h1>
      <v-btn v-if="auth.isSubscribed" color="primary" @click="showUpload = true">
        <v-icon start>mdi-upload</v-icon>
        Upload game
      </v-btn>
    </div>

    <!-- Not subscribed -->
    <v-card
      v-if="!auth.isSubscribed"
      color="surface-variant"
      variant="flat"
      class="pa-8 text-center mb-6"
    >
      <v-icon size="48" color="primary" class="mb-4">mdi-lock</v-icon>
      <h3 class="text-h6 mb-2">Premium required</h3>
      <p class="text-body-2 text-grey mb-4">
        Upgrade to Premium to upload and view your games.
      </p>
      <v-btn color="primary" to="/pricing">Upgrade now</v-btn>
    </v-card>

    <!-- Loading -->
    <v-progress-circular
      v-else-if="games.loading"
      indeterminate
      color="primary"
      class="d-block mx-auto my-8"
    />

    <!-- Empty state -->
    <v-card
      v-else-if="games.games.length === 0"
      color="surface-variant"
      variant="flat"
      class="pa-8 text-center"
    >
      <v-icon size="48" color="grey" class="mb-4">mdi-gamepad-variant-outline</v-icon>
      <h3 class="text-h6 mb-2">No games yet</h3>
      <p class="text-body-2 text-grey">
        Upload your first game to get started.
      </p>
    </v-card>

    <!-- Games list -->
    <v-card v-else color="surface" rounded="lg">
      <v-list bg-color="transparent">
        <v-list-item
          v-for="game in games.games"
          :key="game.gameId"
        >
          <v-list-item-title class="font-weight-medium">
            {{ game.title || "Untitled game" }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ game.playedAt ? new Date(game.playedAt).toLocaleDateString("da-DK") : "Unknown date" }}
            <template v-if="game.opponent"> vs {{ game.opponent }}</template>
          </v-list-item-subtitle>

          <template v-slot:append>
            <v-chip
              v-if="game.result"
              :color="game.result === 'win' ? 'success' : game.result === 'loss' ? 'error' : 'grey'"
              size="small"
              variant="flat"
              class="mr-2"
            >
              {{ game.result === "win" ? "win" : game.result === "loss" ? "loss" : "draw" }}
            </v-chip>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click.stop="confirmDelete(game)"
            >
              <v-icon size="18">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Pagination -->
    <div
      v-if="games.pagination.totalPages > 1"
      class="d-flex justify-center mt-4"
    >
      <v-pagination
        v-model="currentPage"
        :length="games.pagination.totalPages"
        @update:model-value="games.fetchGames($event)"
        rounded="circle"
        color="primary"
      />
    </div>

    <!-- Delete Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="360">
      <v-card class="pa-6">
        <h3 class="text-h6 mb-4">Delete game?</h3>
        <p class="text-body-2 text-grey mb-4">
          This cannot be undone.
        </p>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="doDelete">Delete</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAuthStore } from "../stores/auth";
import { useGamesStore } from "../stores/games";

const auth = useAuthStore();
const games = useGamesStore();

const currentPage = ref(1);
const showUpload = ref(false);
const showDeleteDialog = ref(false);
const gameToDelete = ref(null);

function confirmDelete(game) {
  gameToDelete.value = game;
  showDeleteDialog.value = true;
}

async function doDelete() {
  if (gameToDelete.value) {
    await games.deleteGame(gameToDelete.value.gameId);
  }
  showDeleteDialog.value = false;
  gameToDelete.value = null;
}

onMounted(() => {
  if (auth.isSubscribed) {
    games.fetchGames();
  }
});
</script>
